import { isEqual } from "es-toolkit";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import type { DocumentMetadata } from "@moment/api-collab/api-types";
import { Store as DifferentialStore } from "@moment/api-collab/differential";
import { Logger } from "@moment/logging";
import { RebootQueryPersistence, queryEvaluatorStateFromReboot } from "@moment/reboot-differential";

import { METADATA_BASE_SPEC } from "../document-metadata/metadata-state.atom";
import { type MetadataCollabConnection } from "./collab.types";
import { $singletonRebootContext } from "./reboot-context.atom";
import { type DocumentRefProps, metadataRef } from "./refs";

const logger = new Logger("metadata-collab-connection");

/**
 * The canonical Reboot connection for document metadata, as identified by workspace slug,
 * document ID, branch, and SHA.
 *
 * Returns null if the application-wide singleton Reboot `WebContext` is not available.
 *
 * This atom creates and caches the DifferentialStore for metadata operations.
 */
export const $metadataCollabConnection = atomFamily((props: DocumentRefProps) => {
	const connectionAtom = atom(async (get): Promise<MetadataCollabConnection | null> => {
		// Get the Reboot context
		const context = get($singletonRebootContext);
		if (!context) {
			logger.warn("RebootContext not available", { ...props });
			return null;
		}

		// Create Reboot store reference for this document's metadata
		const rebootStoreRef = metadataRef(props);

		// Create abort controller for the fetch
		const abortController = new AbortController();

		// Fetch initial state from Reboot
		const initialState = await queryEvaluatorStateFromReboot(
			context,
			rebootStoreRef,
			METADATA_BASE_SPEC,
			abortController.signal
		);

		// Create the query persistence layer
		const queryPersistence = new RebootQueryPersistence(context, rebootStoreRef, initialState);

		// Create the differential Store for metadata operations
		const store = new DifferentialStore<{ metadata: DocumentMetadata }>(
			queryPersistence,
			{
				// Base spec for metadata is fixed
				localBaseSpecReplica: () => METADATA_BASE_SPEC,
				saveBaseSpec: async () => {
					// Metadata base spec is immutable
				},
			},
			true // isStoreOwner
		);

		logger.info("Created metadata collab connection", { ...props });

		return { rebootStoreRef, store, context };
	});

	connectionAtom.debugLabel = `metadataCollabConnection/${props.workspaceSlug}/${props.documentId}/${props.branch}/${props.sha}`;
	return connectionAtom;
}, isEqual);
